/* eslint-disable react-hooks/rules-of-hooks */
import { Alert, Box, Snackbar, Stack } from '@mui/material';
import cloneDeep from 'lodash/cloneDeep';
import moment from 'moment';
import * as React from 'react';
import Loading from '../../../components/atoms/Loading';
import Modal from '../../../controls/Modal';
import { Models } from '../api/api-types';
import EditModel from '../components/EditModel/EditModel';
import Errors from '../components/EditModel/Errors';
import History from '../components/EditModel/History';
import Tabs, { TabType } from '../components/EditModel/Tabs';
import { useGetApiV1UnderwritingModelsQuery } from '../hooks';
import { ModelTypes } from '../types/model';
import { hasModelChanges } from '../utils/hasModelChanges';

interface IAddOrCreateModelViewBaseProps {
    open: boolean;
    title: string;
    onClose: () => void;
}

const AddOrCreateModel: React.FunctionComponent<IAddOrCreateModelViewBaseProps> & {
    Create: React.FunctionComponent<{ type: ModelTypes; onSubmit: (m: Models) => Promise<void> }>;
    Edit: React.FunctionComponent<{
        modelID: number;
        onDelete: (modelID: number) => Promise<void>;
        onSubmit: (m: Models) => Promise<void>;
    }>;
} = ({ onClose, title, open, children }) => {
    return (
        <Modal
            open={open}
            onClose={onClose}
            PaperProps={{ style: { minHeight: 'calc(100vh - 64px)', width: 900, maxWidth: '100%', overflowY: 'hidden' } }}
            maxWidth={'md'}
            title={title}>
            {children}
        </Modal>
    );
};

AddOrCreateModel.Create = ({ type, onSubmit }) => {
    const defaultModel = (t: ModelTypes): any => ({
        name: '',
        modelID: 0,
        sortOrder: -1,
        activeStart: moment().format(),
        activeEnd: moment('12/31/2099', 'MM/DD/YYYY').format(),
        groupID: null,
        groupWeight: null,
        modelType: t,
        lowAmount: 0,
        highAmount: 500,
        originFilters: [],
        stateFilters: [],
        campaignFilters: [],
        preReqs: [],
    });
    return (
        <EditModel type={type} model={defaultModel(type)} onSubmit={onSubmit} />
    );
};

AddOrCreateModel.Edit = ({ modelID, onDelete, onSubmit }) => {
    const { data: models, isLoading } = useGetApiV1UnderwritingModelsQuery(
        { modelId: modelID },
        { refetchOnMountOrArgChange: true },
    );
    // find model
    const model = React.useMemo(() => models?.entities[modelID] as Models, [modelID, models]);
    const [active, setActive] = React.useState<TabType>('detail');
    const originalModelRef = React.useRef<Models | null>(null);
    React.useEffect(() => {
        if (model) {
            originalModelRef.current = cloneDeep(model);
        }
    }, [model]);

    const [noChangesWarningOpen, setNoChangesWarningOpen] = React.useState(false);
    const handleSubmit = React.useCallback(
        async (updatedModel: Models) => {
            const originalModel = originalModelRef.current;
            if (originalModel && !hasModelChanges(originalModel, updatedModel)) {
                setNoChangesWarningOpen(true);
                return;
            }
            await onSubmit(updatedModel);
        },
        [onSubmit],
    );

    return !isLoading && models ? (
        <Box>
            <Stack spacing={1} alignItems="center">
                {moment(model.activeStart).isAfter(moment(model.activeEnd)) && (
                    <Alert severity={'warning'} style={{ marginBottom: 16 }}>
                        This Model is disabled
                    </Alert>
                )}
                <Tabs active={active} onChange={setActive} />
                {active === 'detail' && (
                    <EditModel type={model.modelType} model={model} onSubmit={handleSubmit} onDelete={onDelete} />
                )}
                {active === 'history' && <History model={model} />}
                {active === 'error' && <Errors modelID={modelID} />}
            </Stack>
            <Snackbar
                open={noChangesWarningOpen}
                autoHideDuration={4000}
                onClose={() => setNoChangesWarningOpen(false)}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert severity="warning" onClose={() => setNoChangesWarningOpen(false)} variant="filled">
                    No changes to update
                </Alert>
            </Snackbar>
        </Box>
    ) : (
        <Box sx={{ height: '100vh', width: '100%' }}><Loading /></Box>
    );
};

export default AddOrCreateModel;
